import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewEnterpriseArchitecture = {

    id: 42,
    slug: 'purview-enterprise-architecture',
    ogImage: '/og/courses/purview-enterprise-architecture.jpg',
    courseCode: 'PURVIEW-ARCH-001',
    featured: true,
    title: 'Microsoft Purview Enterprise Architecture',
    subtitle: 'Automate, hunt, and orchestrate Microsoft Purview at 10,000+ user scale',
    organization: 'EDUC4TE',
    valueProposition: 'Take Microsoft Purview from a configured product to an automated, code-driven control plane — multi-cloud architecture patterns with Zero Trust integration, governance automated at scale with PowerShell, Microsoft Graph API, and Azure Functions, policy-as-code CI/CD pipelines, KQL threat hunting across audit logs, and Microsoft Sentinel SOAR playbooks for automated DLP incident response.',
    duration: '8 hours',
    students: 'New',
    level: 'Advanced',
    image: ImagePaths.COURSE_4,
    shortDescription: 'PURVIEW-ARCH-001 — Purview enterprise architecture covering multi-cloud patterns, PowerShell/Graph automation, policy-as-code, and Sentinel SOAR for architects.',
    price: {
      aud: 1500,
      currency: 'AUD'
    },

    stripeConfig: {
    priceId: 'price_1Te5C5Ku1nyc322UONTEu7FZ',
    productId: 'prod_UapkdZWUNarQzU'
  },

    snapshot: {
      whoItsFor: [
        'Enterprise architects designing multi-cloud Purview architecture patterns with Zero Trust integration',
        'Principal security engineers automating governance at scale with PowerShell, Graph API, and Azure Functions',
        'DevOps and platform engineers building policy-as-code CI/CD pipelines for continuous compliance',
        'Threat hunters writing advanced KQL across Purview audit logs and integrating Microsoft Sentinel',
        'SecOps architects designing SOAR playbooks for automated DLP incident response'
      ],
      accreditation: 'SC-401 100% coverage; positions for Microsoft Cybersecurity Architect Expert (SC-100) when combined with SC-200 or SC-300'
    },

    logistics: {
      classSize: 'One-day architect intensive — small architect cohort with live design reviews, automation walk-throughs, and hands-on KQL and SOAR labs',
      requirements: [
        'Completion of the three EDUC4TE Purview practitioner modules (Modules 2 through 5) or equivalent enterprise-scale Purview experience',
        'Strong PowerShell scripting and Microsoft Graph API knowledge',
        'Working knowledge of Microsoft Entra ID, Conditional Access, and Zero Trust principles',
        'Familiarity with Azure Functions and CI/CD pipeline concepts (Azure DevOps or GitHub Actions)',
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for automation and hunting labs',
        'Awareness of the Australian regulatory landscape (Privacy Act 1988, ACSC ISM, PSPF, APRA CPS 234)'
      ]
    },

    overview: 'Microsoft Purview Enterprise Architecture is the capstone of the EDUC4TE Purview Training Track — Module 6 of 6 — and the only course in the programme built for architects rather than practitioners. Across a single intensive day (8 hours) you will design multi-cloud Purview architecture patterns with Zero Trust integration, then automate governance at scale using PowerShell, the Microsoft Graph API, and Azure Functions. You will implement policy-as-code with CI/CD pipelines for continuous compliance deployment, write advanced KQL queries to hunt threats across Purview audit logs, and integrate Microsoft Sentinel to design SOAR playbooks for automated DLP incident response. The day is reference-architecture and automation led: licensing optimisation and a reference architecture for 10,000+ user scale frame the morning, with policy-as-code, threat hunting, and SOAR orchestration filling the afternoon. Material is grounded in current Microsoft Learn Purview deployment guidance, the SC-401 Information Security Administrator objectives (100% coverage), and — when combined with SC-200 or SC-300 — the Microsoft Cybersecurity Architect Expert (SC-100) exam, alongside the ACSC Information Security Manual and the Protective Security Policy Framework.',

    outcomes: [
      'Design multi-cloud Purview architecture patterns with Zero Trust integration',
      'Automate governance at scale using PowerShell, Microsoft Graph API, and Azure Functions',
      'Implement policy-as-code with CI/CD pipelines for continuous compliance deployment',
      'Write advanced KQL queries for threat hunting across Purview audit logs',
      'Integrate Microsoft Sentinel and design SOAR playbooks for automated DLP incident response'
    ],

    audience: [
      'Enterprise architects designing multi-cloud Purview reference architectures with Zero Trust integration',
      'Principal security engineers automating governance with PowerShell, Graph API, and Azure Functions',
      'DevOps and platform engineers building policy-as-code CI/CD pipelines',
      'Threat hunters and SecOps architects writing KQL and designing Sentinel SOAR playbooks',
      'Microsoft Partner Solution Architects delivering automated Purview at enterprise scale',
      'CISOs with technical depth owning data security automation and continuous compliance'
    ],

    prerequisites: [
      'Completion of the three EDUC4TE Purview practitioner modules (Modules 2 through 5) OR equivalent enterprise-scale Purview experience',
      'Strong PowerShell scripting and Microsoft Graph API knowledge',
      'Working knowledge of Microsoft Entra ID, Conditional Access, and Zero Trust principles',
      'Familiarity with Azure Functions and CI/CD pipelines (Azure DevOps or GitHub Actions)',
      'Comfort working at reference-architecture depth — diagrams, control flows, and operating models for 10,000+ user scale',
      'Awareness of the Australian regulatory landscape (Privacy Act 1988, ACSC ISM, PSPF, APRA CPS 234)'
    ],

    modules: [
      {
        number: 1,
        title: 'Architecture design patterns',
        duration: '120 min',
        topics: [
          'Design multi-cloud Microsoft Purview architecture patterns with Zero Trust integration',
          'Licensing optimisation — mapping E5, Compliance add-on, and Copilot economics to the architecture',
          'Reference architecture for 10,000+ user scale — control plane, data plane, and evidence plane separation',
          'Integrating Purview signal with Entra Conditional Access and Defender XDR under a Zero Trust model',
          'Reference: Microsoft Learn — Microsoft Purview deployment guidance (updated 2025)'
        ]
      },
      {
        number: 2,
        title: 'Automation and DevOps',
        duration: '180 min',
        topics: [
          'Automate governance at scale using PowerShell, the Microsoft Graph API, and Azure Functions',
          'Implement policy-as-code — version-controlled Purview policy definitions and review workflows',
          'CI/CD pipelines for continuous compliance deployment (Azure DevOps or GitHub Actions)',
          'Idempotent deployment patterns, drift detection, and automated rollback for compliance policy',
          'Reference: Microsoft Learn — Microsoft Graph API and Azure Functions for Purview automation (current)'
        ]
      },
      {
        number: 3,
        title: 'Advanced hunting and SOAR',
        duration: '180 min',
        topics: [
          'Write advanced KQL queries for threat hunting across Purview audit logs',
          'Integrate Microsoft Sentinel — ingesting Purview signals and building analytics rules',
          'Design SOAR playbooks for automated DLP incident response',
          'Automated triage, enrichment, and containment workflows for data security incidents',
          'Reference: Microsoft Learn — KQL for advanced hunting and Microsoft Sentinel SOAR (current)'
        ]
      }
    ],

    format: {
      type: 'One-day architect intensive (virtual), or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of architect-led training in a single intensive day',
        'Multi-cloud Purview reference architecture diagrams with Zero Trust integration',
        'Reference architecture for 10,000+ user scale and licensing optimisation worksheet',
        'PowerShell, Microsoft Graph API, and Azure Functions automation scripts',
        'Policy-as-code CI/CD pipeline templates (Azure DevOps and GitHub Actions)',
        'Advanced KQL threat-hunting query library for Purview audit logs',
        'Microsoft Sentinel SOAR playbook templates for automated DLP incident response',
        'Certificate of completion suitable for CPD claims and architect portfolio evidence'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Small architect-tier cohort to support live design reviews and hands-on automation labs — register interest for the next one-day intensive'
    },

    certification: 'Educ4te Certificate of Completion — Microsoft Purview Enterprise Architecture. Capstone of the EDUC4TE Purview Training Track (Module 6 of 6). Delivers 100% coverage of SC-401 Information Security Administrator objectives, and positions for Microsoft Cybersecurity Architect Expert (SC-100) when combined with SC-200 or SC-300.',

    faqs: [
      {
        question: 'What does the day actually cover?',
        answer: 'Three hands-on labs across 8 hours: architecture design patterns (2 hours) — multi-cloud Purview with Zero Trust integration, licensing optimisation, and a reference architecture for 10,000+ user scale; automation and DevOps (3 hours) — automating governance with PowerShell, the Microsoft Graph API, and Azure Functions, plus policy-as-code with CI/CD pipelines for continuous compliance; and advanced hunting and SOAR (3 hours) — KQL threat hunting across Purview audit logs and Microsoft Sentinel SOAR playbooks for automated DLP incident response.'
      },
      {
        question: 'What are the prerequisites?',
        answer: 'Completion of the three EDUC4TE Purview practitioner modules (Modules 2 through 5) or equivalent enterprise-scale Purview experience, plus strong PowerShell and Microsoft Graph API knowledge. This is an architect intensive — we assume you can already operate Purview and script against it, and we focus on automating, hunting, and orchestrating it at scale.'
      },
      {
        question: 'How does this align with Microsoft certification?',
        answer: 'The course delivers 100% coverage of the SC-401 Information Security Administrator objectives. Combined with SC-200 (Security Operations Analyst) or SC-300 (Identity and Access Administrator), it also positions you for the Microsoft Cybersecurity Architect Expert (SC-100) by grounding the data security automation and threat-hunting portions of that exam.'
      },
      {
        question: 'Why is this only 8 hours when it is an architect-tier course?',
        answer: 'Because it assumes the practitioner depth from Modules 2 through 5 is already in place. Rather than re-teaching Purview features, this capstone concentrates on the architect-tier delta — design patterns, automation and DevOps, and advanced hunting and SOAR — in three focused hands-on labs across a single intensive day.'
      },
      {
        question: 'Do I need to have completed earlier EDUC4TE Purview courses?',
        answer: 'The three practitioner modules (Modules 2 through 5) are the expected path, but equivalent enterprise-scale Purview experience is accepted. Either way you need strong PowerShell and Microsoft Graph API skills, because the automation and DevOps labs assume you can read and extend scripts rather than write your first one on the day.'
      },
      {
        question: 'Does the course still cover Australian compliance frameworks?',
        answer: 'Yes. The reference architecture, policy-as-code, and SOAR work are framed against the ACSC Information Security Manual, the Protective Security Policy Framework, Essential Eight, Australian Privacy Principle 11, and APRA CPS 234 — so the automation and threat-hunting patterns you build map directly to the obligations Australian enterprise and government carry.'
      },
      {
        question: 'Are partner and architect group rates available?',
        answer: 'Yes. Microsoft Partner Solution Architects, federal and state government architect teams, and enterprise architect cohorts of four or more can request a private quote. Contact us with cohort size and target intake date.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'ACSC ISM', 'ACSC PSPF', 'Essential Eight', 'APRA CPS 234']
};
